/**
 * Mixin para manejo de resultados de DataFast (Payment y Tokenization)
 * Elimina código duplicado entre DataFastPayment, DataFastTokenization y DataFastSubscription
 */

export default {
    methods: {
        /**
         * Verifica si hay parámetros de resultado de DataFast en la URL
         * @param {String} sessionKey - Clave de sessionStorage ('datafast_payment' o 'datafast_tokenization')
         * @returns {Object|null} - Datos del resultado o null
         */
        checkDataFastURLParams(sessionKey) {
            const urlParams = new URLSearchParams(window.location.search)
            const resourcePath = urlParams.get('resourcePath')
            const id = urlParams.get('id')

            if (resourcePath || id) {
                const savedData = sessionStorage.getItem(sessionKey)
                if (savedData) {
                    return {
                        resourcePath:
                            resourcePath ||
                            `/v1/checkouts/${id}/${
                                sessionKey.includes('payment')
                                    ? 'payment'
                                    : 'registration'
                            }`,
                        savedData: JSON.parse(savedData),
                    }
                }
            }

            return null
        },

        /**
         * Limpia la URL de parámetros de DataFast
         */
        cleanDataFastURL() {
            window.history.replaceState(
                {},
                document.title,
                window.location.pathname
            )
        },

        /**
         * Limpia sessionStorage de DataFast
         * @param {String} sessionKey - Clave a limpiar
         */
        cleanDataFastSession(sessionKey) {
            sessionStorage.removeItem(sessionKey)
        },

        /**
         * Limpia completamente el estado de DataFast (URL + Session + Scripts)
         * @param {String} sessionKey - Clave de sessionStorage
         */
        cleanupDataFast(sessionKey) {
            this.cleanDataFastSession(sessionKey)
            this.cleanDataFastURL()
            this.removeDataFastScripts()
        },

        /**
         * Remueve scripts de DataFast del DOM
         */
        removeDataFastScripts(preserveOptions = false) {
            const script = document.querySelector(
                'script[src*="paymentWidgets.js"]'
            )
            if (script) {
                script.remove()
            }

            // Limpiar opciones globales si existen
            if (!preserveOptions && window.wpwlOptions) {
                delete window.wpwlOptions
            }
        },

        /**
         * Guarda datos en sessionStorage para recuperar después del redirect
         * @param {String} sessionKey - Clave de sessionStorage
         * @param {Object} data - Datos a guardar
         */
        saveDataFastSession(sessionKey, data) {
            sessionStorage.setItem(sessionKey, JSON.stringify(data))
        },

        /**
         * Maneja errores de forma centralizada
         * @param {Error} error - Error capturado
         * @param {String} defaultMessage - Mensaje por defecto
         * @returns {String} - Mensaje de error
         */
        handleDataFastError(error, defaultMessage = 'Error al procesar') {
            return (
                error.response?.data?.error || error.message || defaultMessage
            )
        },

        /**
         * Extrae código de error DataFast desde el mensaje de error del backend
         * @param {Error} error - Error capturado
         * @returns {String|null} Código de error si existe
         */
        extractDataFastErrorCode(error) {
            const rawMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                ''
            if (!rawMessage) return null

            const match =
                rawMessage.match(/"code"\s*:\s*"([^"]+)"/i) ||
                rawMessage.match(/code\s*[:\s"]+([0-9.]+)/i)
            return match ? match[1] : null
        },

        /**
         * Valida que el checkoutId/registrationId esté presente
         * @param {String} id - ID a validar
         * @param {String} type - Tipo ('checkoutId' o 'registrationId')
         * @throws {Error} - Si no está presente
         */
        validateDataFastId(id, type = 'checkoutId') {
            if (!id) {
                throw new Error(`No se recibió ${type} del servidor`)
            }
        },
    },
}
